import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import {
  LogOut, Loader2, Users, CreditCard, FileCheck, FileText, BarChart3,
  Mail, Shield, Eye, Download, ChevronLeft, UserPlus, Trash2, Send, UsersRound, Plus, Clock
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/admin`;

// --- Admin Login ---
const AdminLogin = ({ onLogin }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const res = await axios.post(`${API}/login`, { email, password });
      onLogin(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Error de autenticacion');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: '#0B1D3A' }}>
      <Card className="w-full max-w-md" style={{ border: '1px solid rgba(197,165,90,0.3)' }}>
        <CardHeader className="text-center">
          <div className="mb-2">
            <Shield className="w-10 h-10 mx-auto" style={{ color: '#C5A55A' }} />
          </div>
          <CardTitle className="text-2xl" style={{ fontFamily: "'Playfair Display', Georgia, serif", color: '#0B1D3A' }}>
            Panel de Administracion
          </CardTitle>
          <p className="text-sm text-gray-500">CERTILEX — Acceso restringido</p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>Email</Label>
              <Input type="email" value={email} onChange={e => setEmail(e.target.value)} required data-testid="admin-login-email" />
            </div>
            <div className="space-y-2">
              <Label>Contrasena</Label>
              <Input type="password" value={password} onChange={e => setPassword(e.target.value)} required data-testid="admin-login-password" />
            </div>
            {error && <p className="text-red-600 text-sm">{error}</p>}
            <Button type="submit" className="w-full" style={{ background: '#C5A55A', color: '#0B1D3A', fontWeight: 700 }} disabled={loading} data-testid="admin-login-submit">
              {loading ? 'Accediendo...' : 'Acceder'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

// --- Stats Cards ---
const StatsPanel = ({ stats }) => {
  const cards = [
    { label: 'Profesionales', value: stats.professionals, icon: Users, color: '#2563eb' },
    { label: 'Pedidos pagados', value: stats.paid_orders, icon: CreditCard, color: '#16a34a' },
    { label: 'Ingresos', value: `${(stats.revenue || 0).toLocaleString('es-ES')} EUR`, icon: BarChart3, color: '#C5A55A' },
    { label: 'Certificaciones pendientes', value: stats.pending_certifications, icon: FileCheck, color: '#ea580c' },
    { label: 'Documentos', value: stats.documents, icon: FileText, color: '#7c3aed' },
    { label: 'Mensajes contacto', value: stats.contacts, icon: Mail, color: '#0891b2' },
  ];

  return (
    <div className="grid grid-cols-2 lg:grid-cols-3 gap-4" data-testid="admin-stats">
      {cards.map((c, i) => (
        <Card key={i} style={{ borderLeft: `4px solid ${c.color}` }}>
          <CardContent className="p-4 flex items-center gap-3">
            <c.icon className="w-8 h-8 flex-shrink-0" style={{ color: c.color }} />
            <div>
              <p className="text-2xl font-bold" style={{ color: '#0B1D3A' }}>{c.value}</p>
              <p className="text-xs" style={{ color: '#4A4A5A' }}>{c.label}</p>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

// --- Professionals Tab ---
const ProfessionalsTab = ({ token }) => {
  const [pros, setPros] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [sendingWelcome, setSendingWelcome] = useState(null);

  useEffect(() => {
    axios.get(`${API}/professionals`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setPros(r.data))
      .finally(() => setLoading(false));
  }, [token]);

  const viewDetail = async (id) => {
    setSelected(id);
    const res = await axios.get(`${API}/professionals/${id}`, { headers: { Authorization: `Bearer ${token}` } });
    setDetail(res.data);
  };

  const sendWelcome = async (proId, proEmail) => {
    if (!window.confirm(`Enviar email de bienvenida con nuevas credenciales a ${proEmail}?`)) return;
    setSendingWelcome(proId);
    try {
      const res = await axios.post(`${API}/professionals/${proId}/send-welcome`,
        { professional_id: proId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert(res.data.message || 'Email enviado');
    } catch (err) {
      alert(err.response?.data?.detail || 'Error al enviar');
    } finally {
      setSendingWelcome(null);
    }
  };

  if (selected && detail) {
    return (
      <div>
        <Button variant="ghost" onClick={() => { setSelected(null); setDetail(null); }} className="mb-4">
          <ChevronLeft className="w-4 h-4 mr-1" /> Volver
        </Button>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-2">
            <CardTitle style={{ fontFamily: "'Playfair Display', Georgia, serif", color: '#0B1D3A' }}>
              {detail.full_name}
            </CardTitle>
            <div className="flex items-center gap-2 flex-wrap">
              <Button
                size="sm"
                variant="outline"
                onClick={async () => {
                  const res = await axios.put(`${API}/professionals/${detail.id}/toggle-public`, {}, { headers: { Authorization: `Bearer ${token}` } });
                  setDetail({ ...detail, public_profile_enabled: res.data.public_profile_enabled });
                }}
                data-testid="toggle-public-btn"
              >
                {detail.public_profile_enabled ? 'Ocultar perfil' : 'Publicar perfil'}
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={async () => {
                  const comp = prompt('Competencias certificadas (separar con comas):');
                  if (comp === null) return;
                  await axios.put(`${API}/professionals/${detail.id}/set-certified`,
                    { is_certified: true, competencies: comp.split(',').map(c => c.trim()).filter(Boolean) },
                    { headers: { Authorization: `Bearer ${token}` } });
                  viewDetail(detail.id);
                }}
                data-testid="set-certified-btn"
              >
                Certificar
              </Button>
              <Button
                size="sm"
                style={{ background: '#0B1D3A', color: '#fff' }}
                onClick={() => sendWelcome(detail.id, detail.email)}
                disabled={sendingWelcome === detail.id}
                data-testid="send-welcome-btn"
              >
                {sendingWelcome === detail.id ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Send className="w-4 h-4 mr-1" />}
                Email bienvenida
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Status badges */}
            <div className="flex gap-2 flex-wrap">
              {detail.is_certified && <Badge className="bg-green-100 text-green-800">Certificado</Badge>}
              {detail.public_profile_enabled && <Badge className="bg-blue-100 text-blue-800">Perfil publico</Badge>}
              {detail.doc_processing_status === 'completed' && <Badge className="bg-amber-100 text-amber-800">Expediente analizado</Badge>}
              {detail.doc_processing_status === 'processing' && <Badge className="bg-yellow-100 text-yellow-800">Procesando...</Badge>}
            </div>

            <div className="grid md:grid-cols-2 gap-4 text-sm">
              <div><strong>Email:</strong> {detail.email}</div>
              <div><strong>Telefono:</strong> {detail.phone}</div>
              <div><strong>Especialidad:</strong> {detail.specialty}</div>
              <div><strong>Pais:</strong> {detail.country}</div>
              <div><strong>Colegiado:</strong> {detail.collegiate_number || '—'}</div>
              <div><strong>Colegio:</strong> {detail.official_college || '—'}</div>
              <div><strong>Registrado:</strong> {String(detail.created_at).slice(0, 10)}</div>
              <div><strong>Certificado:</strong> {detail.is_certified ? 'Si' : 'No'}</div>
            </div>

            {/* Expedition Summary (IA Analysis) */}
            {detail.expedition_summary && (
              <div className="p-4 rounded-lg" style={{ background: '#FFF7ED', border: '1px solid #C5A55A' }}>
                <h4 className="font-semibold text-sm mb-2" style={{ color: '#0B1D3A' }}>Analisis IA del Expediente</h4>
                <p className="text-sm mb-2" style={{ color: '#4A4A5A' }}>{detail.expedition_summary.valoracion_global}</p>
                {detail.expedition_summary.competencias_acreditables?.length > 0 && (
                  <div className="mb-2">
                    <strong className="text-xs">Competencias acreditables:</strong>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {detail.expedition_summary.competencias_acreditables.map((c, i) => <Badge key={i} variant="outline" className="text-xs">{c}</Badge>)}
                    </div>
                  </div>
                )}
                {detail.expedition_summary.documentos_faltantes?.length > 0 && (
                  <div>
                    <strong className="text-xs" style={{ color: '#c0392b' }}>Documentacion faltante:</strong>
                    <ul className="text-xs mt-1" style={{ color: '#4A4A5A' }}>
                      {detail.expedition_summary.documentos_faltantes.map((d, i) => <li key={i}>- {d}</li>)}
                    </ul>
                  </div>
                )}
                <div className="mt-2 text-right">
                  <Badge className={detail.expedition_summary.apto_para_certificacion ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}>
                    {detail.expedition_summary.apto_para_certificacion ? 'APTO' : 'PENDIENTE'}
                  </Badge>
                </div>
              </div>
            )}
            {detail.areas_to_certify?.length > 0 && (
              <div>
                <strong className="text-sm">Areas a certificar:</strong>
                <div className="flex flex-wrap gap-1 mt-1">
                  {detail.areas_to_certify.map((a, i) => <Badge key={i} variant="outline">{a}</Badge>)}
                </div>
              </div>
            )}
            {detail.documents?.length > 0 && (
              <div>
                <h4 className="font-semibold text-sm mt-4 mb-2">Documentos ({detail.documents.length})</h4>
                {detail.documents.map((d, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm p-2 rounded" style={{ background: '#F6F5F0' }}>
                    <FileText className="w-4 h-4" style={{ color: '#C5A55A' }} />
                    <span>{d.filename}</span>
                    <Badge variant="outline" className="text-xs">{d.document_type}</Badge>
                  </div>
                ))}
              </div>
            )}
            {detail.orders?.length > 0 && (
              <div>
                <h4 className="font-semibold text-sm mt-4 mb-2">Pedidos ({detail.orders.length})</h4>
                {detail.orders.map((o, i) => (
                  <div key={i} className="flex items-center justify-between text-sm p-2 rounded" style={{ background: '#F6F5F0' }}>
                    <span>{o.invoice_number} — {o.service_name}</span>
                    <div className="flex items-center gap-2">
                      <span className="font-bold">{o.amount} EUR</span>
                      <Badge className={o.status === 'paid' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}>{o.status === 'paid' ? 'Pagado' : 'Pendiente'}</Badge>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {detail.certification_requests?.length > 0 && (
              <div>
                <h4 className="font-semibold text-sm mt-4 mb-2">Certificaciones ({detail.certification_requests.length})</h4>
                {detail.certification_requests.map((c, i) => (
                  <div key={i} className="text-sm p-2 rounded" style={{ background: '#F6F5F0' }}>
                    <span>Areas: {c.areas_to_certify?.join(', ')}</span>
                    <Badge className="ml-2">{c.status}</Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) return <div className="flex justify-center py-8"><Loader2 className="w-8 h-8 animate-spin" style={{ color: '#C5A55A' }} /></div>;

  return (
    <Card>
      <CardHeader>
        <CardTitle style={{ fontFamily: "'Playfair Display', Georgia, serif", color: '#0B1D3A' }}>Profesionales Registrados ({pros.length})</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="admin-professionals-table">
            <thead>
              <tr style={{ background: '#0B1D3A', color: '#fff' }}>
                <th className="p-3 text-left">Nombre</th>
                <th className="p-3 text-left">Email</th>
                <th className="p-3 text-left">Especialidad</th>
                <th className="p-3 text-left">Colegiado</th>
                <th className="p-3 text-left">Registro</th>
                <th className="p-3 text-center">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {pros.map((p, i) => (
                <tr key={i} className="border-b hover:bg-gray-50" style={{ borderColor: '#E2DDD3' }}>
                  <td className="p-3 font-medium">{p.full_name}</td>
                  <td className="p-3">{p.email}</td>
                  <td className="p-3">{p.specialty}</td>
                  <td className="p-3">{p.collegiate_number || '—'}</td>
                  <td className="p-3">{String(p.created_at).slice(0, 10)}</td>
                  <td className="p-3 text-center">
                    <div className="flex items-center justify-center gap-1">
                      <Button size="sm" variant="outline" onClick={() => viewDetail(p.id)} data-testid={`view-pro-${i}`}>
                        <Eye className="w-4 h-4 mr-1" /> Ver
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => sendWelcome(p.id, p.email)} disabled={sendingWelcome === p.id} data-testid={`welcome-pro-${i}`} title="Enviar email bienvenida">
                        {sendingWelcome === p.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
};

// --- Orders Tab ---
const OrdersTab = ({ token }) => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/orders`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setOrders(r.data))
      .finally(() => setLoading(false));
  }, [token]);

  const downloadInvoice = async (orderId, invoiceNumber) => {
    try {
      const res = await axios.get(`${API}/orders/${orderId}/invoice-pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Factura_${invoiceNumber}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert('Error al descargar factura');
    }
  };

  if (loading) return <div className="flex justify-center py-8"><Loader2 className="w-8 h-8 animate-spin" style={{ color: '#C5A55A' }} /></div>;

  return (
    <Card>
      <CardHeader>
        <CardTitle style={{ fontFamily: "'Playfair Display', Georgia, serif", color: '#0B1D3A' }}>Pedidos ({orders.length})</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="admin-orders-table">
            <thead>
              <tr style={{ background: '#0B1D3A', color: '#fff' }}>
                <th className="p-3 text-left">Factura</th>
                <th className="p-3 text-left">Cliente</th>
                <th className="p-3 text-left">Servicio</th>
                <th className="p-3 text-right">Importe</th>
                <th className="p-3 text-center">Estado</th>
                <th className="p-3 text-left">Fecha</th>
                <th className="p-3 text-center">PDF</th>
              </tr>
            </thead>
            <tbody>
              {orders.map((o, i) => (
                <tr key={i} className="border-b hover:bg-gray-50" style={{ borderColor: '#E2DDD3' }}>
                  <td className="p-3 font-mono text-xs">{o.invoice_number}</td>
                  <td className="p-3">
                    <div>{o.user_name || o.billing?.name}</div>
                    <div className="text-xs text-gray-400">{o.user_email}</div>
                  </td>
                  <td className="p-3 text-xs">{o.service_name}</td>
                  <td className="p-3 text-right font-bold">{o.amount} EUR</td>
                  <td className="p-3 text-center">
                    <Badge className={o.status === 'paid' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}>
                      {o.status === 'paid' ? 'Pagado' : 'Pendiente'}
                    </Badge>
                  </td>
                  <td className="p-3 text-xs">{o.created_at?.slice(0, 10)}</td>
                  <td className="p-3 text-center">
                    {o.status === 'paid' && (
                      <Button size="sm" variant="outline" onClick={() => downloadInvoice(o.id, o.invoice_number)}>
                        <Download className="w-4 h-4" />
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
};

// --- Certifications Tab ---
const CertificationsTab = ({ token }) => {
  const [certs, setCerts] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchCerts = useCallback(() => {
    setLoading(true);
    axios.get(`${API}/certifications`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setCerts(r.data))
      .finally(() => setLoading(false));
  }, [token]);

  useEffect(() => { fetchCerts(); }, [fetchCerts]);

  const updateStatus = async (certId, newStatus, notes) => {
    try {
      await axios.put(`${API}/certifications/${certId}/status`,
        { status: newStatus, reviewer_notes: notes || null },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchCerts();
    } catch (err) {
      alert(err.response?.data?.detail || 'Error');
    }
  };

  const statusOptions = ['pendiente', 'en_revision', 'aprobada', 'rechazada', 'certificada'];
  const statusColors = {
    pendiente: 'bg-yellow-100 text-yellow-800',
    en_revision: 'bg-blue-100 text-blue-800',
    aprobada: 'bg-green-100 text-green-800',
    rechazada: 'bg-red-100 text-red-800',
    certificada: 'bg-amber-100 text-amber-800',
  };

  if (loading) return <div className="flex justify-center py-8"><Loader2 className="w-8 h-8 animate-spin" style={{ color: '#C5A55A' }} /></div>;

  return (
    <Card>
      <CardHeader>
        <CardTitle style={{ fontFamily: "'Playfair Display', Georgia, serif", color: '#0B1D3A' }}>Solicitudes de Certificacion ({certs.length})</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {certs.length === 0 ? (
          <p className="text-center text-gray-400 py-6">No hay solicitudes de certificacion.</p>
        ) : certs.map((c, i) => (
          <div key={i} className="p-4 rounded-lg" style={{ background: '#F6F5F0', border: '1px solid #E2DDD3' }}>
            <div className="flex items-start justify-between mb-2">
              <div>
                <p className="font-semibold" style={{ color: '#0B1D3A' }}>{c.professional?.full_name || 'Desconocido'}</p>
                <p className="text-xs text-gray-500">{c.professional?.email}</p>
                <p className="text-sm mt-1">Areas: {c.areas_to_certify?.join(', ')}</p>
                {c.additional_notes && <p className="text-xs text-gray-500 mt-1">Notas: {c.additional_notes}</p>}
                <p className="text-xs text-gray-400 mt-1">Enviado: {String(c.submitted_at).slice(0, 10)}</p>
              </div>
              <Badge className={statusColors[c.status] || 'bg-gray-100'}>{c.status}</Badge>
            </div>
            <div className="flex items-center gap-2 mt-3 flex-wrap">
              {statusOptions.map(s => (
                <Button
                  key={s}
                  size="sm"
                  variant={c.status === s ? 'default' : 'outline'}
                  className="text-xs"
                  style={c.status === s ? { background: '#C5A55A', color: '#0B1D3A' } : {}}
                  onClick={() => {
                    const notes = s === 'rechazada' ? prompt('Motivo del rechazo:') : null;
                    updateStatus(c.id, s, notes);
                  }}
                  disabled={c.status === s}
                >
                  {s}
                </Button>
              ))}
            </div>
            {c.reviewer_notes && (
              <p className="text-xs mt-2 p-2 rounded bg-blue-50 text-blue-800">Revisor: {c.reviewer_notes}</p>
            )}
          </div>
        ))}
      </CardContent>
    </Card>
  );
};

// --- Contacts Tab ---
const ContactsTab = ({ token }) => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/contacts`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setContacts(r.data))
      .finally(() => setLoading(false));
  }, [token]);

  if (loading) return <div className="flex justify-center py-8"><Loader2 className="w-8 h-8 animate-spin" style={{ color: '#C5A55A' }} /></div>;

  return (
    <Card>
      <CardHeader>
        <CardTitle style={{ fontFamily: "'Playfair Display', Georgia, serif", color: '#0B1D3A' }}>Mensajes de Contacto ({contacts.length})</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {contacts.length === 0 ? (
          <p className="text-center text-gray-400 py-6">No hay mensajes.</p>
        ) : contacts.map((c, i) => (
          <div key={i} className="p-4 rounded-lg" style={{ background: '#F6F5F0', border: '1px solid #E2DDD3' }}>
            <div className="flex items-start justify-between">
              <div>
                <p className="font-semibold" style={{ color: '#0B1D3A' }}>{c.name}</p>
                <p className="text-xs text-gray-500">{c.email}</p>
              </div>
              <span className="text-xs text-gray-400">{String(c.created_at).slice(0, 10)}</span>
            </div>
            <p className="text-sm mt-2" style={{ color: '#4A4A5A' }}>{c.message}</p>
          </div>
        ))}
      </CardContent>
    </Card>
  );
};

// --- Operators Tab (superadmin only) ---
const OperatorsTab = ({ token, role }) => {
  const [operators, setOperators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ email: '', name: '', password: '', role: 'operator' });

  const fetchOps = useCallback(() => {
    setLoading(true);
    axios.get(`${API}/operators`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setOperators(r.data))
      .finally(() => setLoading(false));
  }, [token]);

  useEffect(() => { fetchOps(); }, [fetchOps]);

  const createOp = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/operators`, form, { headers: { Authorization: `Bearer ${token}` } });
      setShowForm(false);
      setForm({ email: '', name: '', password: '', role: 'operator' });
      fetchOps();
    } catch (err) {
      alert(err.response?.data?.detail || 'Error');
    }
  };

  const deleteOp = async (id) => {
    if (!window.confirm('Eliminar este operador?')) return;
    try {
      await axios.delete(`${API}/operators/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      fetchOps();
    } catch (err) {
      alert(err.response?.data?.detail || 'Error');
    }
  };

  if (loading) return <div className="flex justify-center py-8"><Loader2 className="w-8 h-8 animate-spin" style={{ color: '#C5A55A' }} /></div>;

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle style={{ fontFamily: "'Playfair Display', Georgia, serif", color: '#0B1D3A' }}>Administradores / Operadores</CardTitle>
        {role === 'superadmin' && (
          <Button size="sm" style={{ background: '#C5A55A', color: '#0B1D3A' }} onClick={() => setShowForm(!showForm)} data-testid="add-operator-btn">
            <UserPlus className="w-4 h-4 mr-1" /> Nuevo
          </Button>
        )}
      </CardHeader>
      <CardContent className="space-y-4">
        {showForm && (
          <form onSubmit={createOp} className="p-4 rounded-lg space-y-3" style={{ background: '#F6F5F0', border: '1px solid #E2DDD3' }}>
            <div className="grid md:grid-cols-2 gap-3">
              <div><Label className="text-xs">Nombre</Label><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required /></div>
              <div><Label className="text-xs">Email</Label><Input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} required /></div>
              <div><Label className="text-xs">Contrasena</Label><Input type="password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} required /></div>
              <div>
                <Label className="text-xs">Rol</Label>
                <select value={form.role} onChange={e => setForm({ ...form, role: e.target.value })} className="w-full px-3 py-2 border rounded-md text-sm" style={{ borderColor: '#E2DDD3' }}>
                  <option value="operator">Operador</option>
                  <option value="superadmin">Superadmin</option>
                </select>
              </div>
            </div>
            <Button type="submit" size="sm" style={{ background: '#C5A55A', color: '#0B1D3A' }}>Crear</Button>
          </form>
        )}
        {operators.map((op, i) => (
          <div key={i} className="flex items-center justify-between p-3 rounded-lg" style={{ background: '#F6F5F0', border: '1px solid #E2DDD3' }}>
            <div>
              <p className="font-semibold text-sm" style={{ color: '#0B1D3A' }}>{op.name}</p>
              <p className="text-xs text-gray-500">{op.email}</p>
            </div>
            <div className="flex items-center gap-2">
              <Badge className={op.role === 'superadmin' ? 'bg-amber-100 text-amber-800' : 'bg-blue-100 text-blue-800'}>{op.role}</Badge>
              {role === 'superadmin' && (
                <Button size="sm" variant="ghost" onClick={() => deleteOp(op.id)} className="text-red-500 hover:text-red-700">
                  <Trash2 className="w-4 h-4" />
                </Button>
              )}
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
};

// --- Main Admin Panel ---
const AdminPanel = () => {
  const [auth, setAuth] = useState(null);
  const [stats, setStats] = useState(null);
  const [activeTab, setActiveTab] = useState('stats');

  // Check stored admin session
  useEffect(() => {
    const stored = localStorage.getItem('admin_token');
    const storedRole = localStorage.getItem('admin_role');
    const storedName = localStorage.getItem('admin_name');
    if (stored) {
      setAuth({ access_token: stored, role: storedRole, name: storedName });
    }
  }, []);

  const handleLogin = (data) => {
    localStorage.setItem('admin_token', data.access_token);
    localStorage.setItem('admin_role', data.role);
    localStorage.setItem('admin_name', data.name);
    setAuth(data);
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_role');
    localStorage.removeItem('admin_name');
    setAuth(null);
    setStats(null);
  };

  // Load stats when authenticated
  useEffect(() => {
    if (auth?.access_token) {
      axios.get(`${API}/stats`, { headers: { Authorization: `Bearer ${auth.access_token}` } })
        .then(r => setStats(r.data))
        .catch(() => handleLogout());
    }
  }, [auth]);

  if (!auth) return <AdminLogin onLogin={handleLogin} />;

  return (
    <div className="min-h-screen" style={{ background: '#F6F5F0' }}>
      {/* Header */}
      <header style={{ background: '#0B1D3A' }}>
        <div className="container mx-auto px-6 py-4 flex justify-between items-center">
          <div>
            <h1 className="text-xl font-bold" style={{ fontFamily: "'Playfair Display', Georgia, serif", color: '#fff', letterSpacing: '2px' }}>
              CERTILEX<sup style={{ fontSize: '0.5em' }}>&reg;</sup> <span style={{ color: '#C5A55A', fontSize: '0.7em', fontWeight: 400 }}>Admin</span>
            </h1>
          </div>
          <div className="flex items-center gap-3">
            <Badge className={auth.role === 'superadmin' ? 'bg-amber-100 text-amber-800' : 'bg-blue-100 text-blue-800'}>{auth.role}</Badge>
            <span className="text-sm text-white">{auth.name}</span>
            <Button variant="outline" size="sm" onClick={handleLogout} style={{ borderColor: 'rgba(255,255,255,0.2)', color: '#fff' }} data-testid="admin-logout-btn">
              <LogOut className="w-4 h-4 mr-1" /> Salir
            </Button>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-6 py-6">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className={`grid w-full max-w-5xl mb-6 ${auth.role === 'superadmin' ? 'grid-cols-8' : 'grid-cols-7'}`}>
            <TabsTrigger value="stats" data-testid="admin-tab-stats"><BarChart3 className="w-4 h-4 mr-1" />Resumen</TabsTrigger>
            <TabsTrigger value="professionals" data-testid="admin-tab-professionals"><Users className="w-4 h-4 mr-1" />Profesionales</TabsTrigger>
            <TabsTrigger value="orders" data-testid="admin-tab-orders"><CreditCard className="w-4 h-4 mr-1" />Pagos</TabsTrigger>
            <TabsTrigger value="groups" data-testid="admin-tab-groups"><UsersRound className="w-4 h-4 mr-1" />Grupos</TabsTrigger>
            <TabsTrigger value="certifications" data-testid="admin-tab-certifications"><FileCheck className="w-4 h-4 mr-1" />Certificaciones</TabsTrigger>
            <TabsTrigger value="documents" data-testid="admin-tab-documents"><FileText className="w-4 h-4 mr-1" />Documentos</TabsTrigger>
            <TabsTrigger value="contacts" data-testid="admin-tab-contacts"><Mail className="w-4 h-4 mr-1" />Contactos</TabsTrigger>
            {auth.role === 'superadmin' && (
              <TabsTrigger value="operators" data-testid="admin-tab-operators"><Shield className="w-4 h-4 mr-1" />Equipo</TabsTrigger>
            )}
          </TabsList>

          <TabsContent value="stats">
            {stats ? <StatsPanel stats={stats} /> : <Loader2 className="w-8 h-8 animate-spin mx-auto" style={{ color: '#C5A55A' }} />}
          </TabsContent>

          <TabsContent value="professionals">
            <ProfessionalsTab token={auth.access_token} />
          </TabsContent>

          <TabsContent value="orders">
            <OrdersTab token={auth.access_token} />
          </TabsContent>

          <TabsContent value="certifications">
            <CertificationsTab token={auth.access_token} />
          </TabsContent>

          <TabsContent value="documents">
            <DocumentsTab token={auth.access_token} />
          </TabsContent>

          <TabsContent value="contacts">
            <ContactsTab token={auth.access_token} />
          </TabsContent>

          <TabsContent value="groups">
            <GroupsTab token={auth.access_token} />
          </TabsContent>

          {auth.role === 'superadmin' && (
            <TabsContent value="operators">
              <OperatorsTab token={auth.access_token} role={auth.role} />
            </TabsContent>
          )}
        </Tabs>
      </div>
    </div>
  );
};

// --- Documents Tab (standalone) ---
const DocumentsTab = ({ token }) => {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/documents`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setDocs(r.data))
      .finally(() => setLoading(false));
  }, [token]);

  const downloadDoc = async (docId, filename) => {
    try {
      const res = await axios.get(`${API}/documents/${docId}/download`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch { alert('Error al descargar'); }
  };

  if (loading) return <div className="flex justify-center py-8"><Loader2 className="w-8 h-8 animate-spin" style={{ color: '#C5A55A' }} /></div>;

  return (
    <Card>
      <CardHeader>
        <CardTitle style={{ fontFamily: "'Playfair Display', Georgia, serif", color: '#0B1D3A' }}>Documentos Subidos ({docs.length})</CardTitle>
      </CardHeader>
      <CardContent>
        {docs.length === 0 ? (
          <p className="text-center text-gray-400 py-6">No hay documentos.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="admin-documents-table">
              <thead>
                <tr style={{ background: '#0B1D3A', color: '#fff' }}>
                  <th className="p-3 text-left">Archivo</th>
                  <th className="p-3 text-left">Tipo</th>
                  <th className="p-3 text-left">Clasificacion</th>
                  <th className="p-3 text-left">Profesional</th>
                  <th className="p-3 text-left">Fecha</th>
                  <th className="p-3 text-center">Descargar</th>
                </tr>
              </thead>
              <tbody>
                {docs.map((d, i) => (
                  <tr key={i} className="border-b" style={{ borderColor: '#E2DDD3' }}>
                    <td className="p-3">{d.filename}</td>
                    <td className="p-3"><Badge variant="outline">{d.document_type}</Badge></td>
                    <td className="p-3">
                      {d.classification ? (
                        <Badge className="bg-green-100 text-green-800">{d.classification}</Badge>
                      ) : (
                        <Badge className="bg-gray-100 text-gray-500">Sin clasificar</Badge>
                      )}
                    </td>
                    <td className="p-3">{d.professional?.full_name || '—'}<br /><span className="text-xs text-gray-400">{d.professional?.email}</span></td>
                    <td className="p-3 text-xs">{String(d.uploaded_at).slice(0, 10)}</td>
                    <td className="p-3 text-center">
                      <Button size="sm" variant="outline" onClick={() => downloadDoc(d.id, d.filename)}>
                        <Download className="w-4 h-4" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AdminPanel;

// --- Groups Tab ---
const GROUPS_API = `${process.env.REACT_APP_BACKEND_URL}/api/groups`;

const GroupsTab = ({ token }) => {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: '', description: '' });

  const headers = { Authorization: `Bearer ${token}` };

  const fetchGroups = useCallback(() => {
    setLoading(true);
    axios.get(`${GROUPS_API}/admin/list`, { headers })
      .then(r => setGroups(r.data))
      .finally(() => setLoading(false));
  }, [token]);

  useEffect(() => { fetchGroups(); }, [fetchGroups]);

  const createGroup = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${GROUPS_API}/create`, form, { headers });
      setShowForm(false);
      setForm({ name: '', description: '' });
      fetchGroups();
    } catch (err) { alert(err.response?.data?.detail || 'Error'); }
  };

  const extendDeadline = async (groupId, days) => {
    try {
      await axios.put(`${GROUPS_API}/admin/${groupId}/extend`, { days }, { headers });
      fetchGroups();
    } catch (err) { alert(err.response?.data?.detail || 'Error'); }
  };

  if (loading) return <div className="flex justify-center py-8"><Loader2 className="w-8 h-8 animate-spin" style={{ color: '#C5A55A' }} /></div>;

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle style={{ fontFamily: "'Playfair Display', Georgia, serif", color: '#0B1D3A' }}>Grupos / Sociedades ({groups.length})</CardTitle>
        <Button size="sm" style={{ background: '#C5A55A', color: '#0B1D3A' }} onClick={() => setShowForm(!showForm)} data-testid="create-group-btn">
          <Plus className="w-4 h-4 mr-1" /> Nuevo grupo
        </Button>
      </CardHeader>
      <CardContent className="space-y-4">
        {showForm && (
          <form onSubmit={createGroup} className="p-4 rounded-lg space-y-3" style={{ background: '#F6F5F0', border: '1px solid #E2DDD3' }}>
            <div className="grid md:grid-cols-2 gap-3">
              <div><Label className="text-xs">Nombre del grupo *</Label><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required placeholder="SECPRE, AECEP..." /></div>
              <div><Label className="text-xs">Descripcion</Label><Input value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Sociedad Espanola de..." /></div>
            </div>
            <Button type="submit" size="sm" style={{ background: '#C5A55A', color: '#0B1D3A' }}>Crear grupo</Button>
          </form>
        )}

        {/* Info box */}
        <div className="p-3 rounded-lg text-xs" style={{ background: '#F6F5F0', border: '1px solid #E2DDD3', color: '#4A4A5A' }}>
          <strong>Descuentos por volumen:</strong> 20+ miembros = 20% devolucion | 50+ miembros = 25% devolucion.
          El refund se aplica automaticamente via Stripe cuando se alcanza el umbral. Plazo: 1 mes desde primer inscrito.
        </div>

        {groups.length === 0 ? (
          <p className="text-center py-6" style={{ color: '#9ca3af' }}>No hay grupos creados.</p>
        ) : groups.map((g, i) => {
          const daysLeft = Math.max(0, Math.ceil((new Date(g.deadline) - new Date()) / (1000 * 60 * 60 * 24)));
          const progressTo20 = Math.min(100, (g.member_count / 20) * 100);
          const progressTo50 = Math.min(100, (g.member_count / 50) * 100);

          return (
            <div key={i} className="p-4 rounded-lg" style={{ background: '#fff', border: '1px solid #E2DDD3' }}>
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h4 className="font-bold" style={{ color: '#0B1D3A' }}>{g.name}</h4>
                  <p className="text-xs" style={{ color: '#4A4A5A' }}>Codigo: <strong>{g.code}</strong></p>
                  {g.description && <p className="text-xs mt-1" style={{ color: '#9ca3af' }}>{g.description}</p>}
                </div>
                <div className="flex items-center gap-2">
                  <Badge className={g.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}>{g.status}</Badge>
                  <Badge className="bg-blue-100 text-blue-800">{g.member_count} miembros</Badge>
                </div>
              </div>

              {/* Progress bars */}
              <div className="space-y-2 mb-3">
                <div>
                  <div className="flex justify-between text-xs mb-1">
                    <span style={{ color: '#4A4A5A' }}>20 miembros (20% dto)</span>
                    <span style={{ color: g.member_count >= 20 ? '#16a34a' : '#C5A55A' }}>{g.member_count}/20 {g.refunds_applied_20 && '— Refunds aplicados'}</span>
                  </div>
                  <div className="h-2 rounded-full" style={{ background: '#E2DDD3' }}>
                    <div className="h-2 rounded-full transition-all" style={{ width: `${progressTo20}%`, background: g.member_count >= 20 ? '#16a34a' : '#C5A55A' }}></div>
                  </div>
                </div>
                <div>
                  <div className="flex justify-between text-xs mb-1">
                    <span style={{ color: '#4A4A5A' }}>50 miembros (25% dto)</span>
                    <span style={{ color: g.member_count >= 50 ? '#16a34a' : '#C5A55A' }}>{g.member_count}/50 {g.refunds_applied_50 && '— Refunds aplicados'}</span>
                  </div>
                  <div className="h-2 rounded-full" style={{ background: '#E2DDD3' }}>
                    <div className="h-2 rounded-full transition-all" style={{ width: `${progressTo50}%`, background: g.member_count >= 50 ? '#16a34a' : '#C5A55A' }}></div>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1 text-xs" style={{ color: daysLeft > 7 ? '#4A4A5A' : '#c0392b' }}>
                  <Clock className="w-3 h-3" /> {daysLeft} dias restantes
                </div>
                <div className="flex items-center gap-1">
                  <Button size="sm" variant="outline" className="text-xs" onClick={() => extendDeadline(g.id, 30)}>+30 dias</Button>
                  <Button size="sm" variant="outline" className="text-xs" onClick={() => { navigator.clipboard.writeText(g.code); alert(`Codigo copiado: ${g.code}`); }}>Copiar codigo</Button>
                </div>
              </div>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
};
